import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { deutscheBahnActionCreators } from "../deutscheBahnModule";
// helpers
import { getUpdatedArray } from "../../../../common/utils/helpers/getUpdatedArray";
import {
  createInitialDeutscheBahnPassenger,
  getDefaultDeutscheBahnPassengerType
} from "../helpers/deutscheBahnPassengerTypes";
import { DEUTSCHE_BAHN_MAX_PASSENGERS } from "../constants";
import type { DeutscheBahnOfferPrice } from "../types";
// components
import {
  PassengerAddButton,
  PassengerColumnHeaders,
  PassengerFormShell,
  PassengerNameFields,
  PassengerRemoveButton,
  PassengerRowMobileDivider
} from "../../shared/passengers";

interface DeutscheBahnPassengersFormProps {
  /** Passenger type options (offer's `offer_prices`). */
  offerPrices: DeutscheBahnOfferPrice[];
  showValidationErrors?: boolean;
}

export default function DeutscheBahnPassengersForm({
  offerPrices,
  showValidationErrors = false
}: DeutscheBahnPassengersFormProps) {
  const { t } = useTranslation();

  const passengerList = useSelector((state: any) => state.deutscheBahn.passengerList);
  const updatePassengers = useActions(deutscheBahnActionCreators?.updatePassengersAction, []);
  const resetPrebookDataInfo = useActions(deutscheBahnActionCreators?.resetPrebookDataInfoAction, []);

  const handlePassengerFirstNameChange = (event: any, index: number) => {
    const updatedList = getUpdatedArray(passengerList, index, "first_name", event.target.value);
    updatePassengers(updatedList);
  };

  const handlePassengerLastNameChange = (event: any, index: number) => {
    const updatedList = getUpdatedArray(passengerList, index, "last_name", event.target.value);
    updatePassengers(updatedList);
  };

  const handlePassengerTypeChange = (index: number, passengerType: string) => {
    const updatedList = getUpdatedArray(passengerList, index, "passenger_type", passengerType);
    updatePassengers(updatedList);
    resetPrebookDataInfo();
  };

  const removePassenger = (index: number) => {
    const updatedList = passengerList?.filter((_passenger: any, i: number) => i !== index);
    updatePassengers(updatedList);
    resetPrebookDataInfo();
  };

  const addPassengerToList = () => {
    if (passengerList.length >= DEUTSCHE_BAHN_MAX_PASSENGERS) {
      return;
    }

    updatePassengers([
      ...passengerList,
      createInitialDeutscheBahnPassenger(getDefaultDeutscheBahnPassengerType(offerPrices), true)
    ]);
    resetPrebookDataInfo();
  };

  return (
    <PassengerFormShell
      title={`${t("transport.passengers")} *`}
      columnHeaders={
        <PassengerColumnHeaders>
          <Grid item xs={3} className="passengers-subtitle" pb={1}>
            {t("transport.formFirstName")}
          </Grid>
          <Grid item xs={3} className="passengers-subtitle" pb={1}>
            {t("transport.formLastName")}
          </Grid>
          <Grid item xs={3} className="passengers-subtitle" pb={1}>
            {t("transport.formPassengerType")}
          </Grid>
        </PassengerColumnHeaders>
      }
      addButton={
        passengerList.length < DEUTSCHE_BAHN_MAX_PASSENGERS ? <PassengerAddButton onClick={addPassengerToList} /> : null
      }
      infoText={t("transport.deutscheBahn.passengersHint")}
    >
      {passengerList?.length > 0 &&
        passengerList?.map((passenger: any, index: number) => {
          return (
            <Grid container columnSpacing={{ xs: 1 }} mb={2} key={index}>
              <PassengerNameFields
                firstName={passenger?.first_name}
                lastName={passenger?.last_name}
                onFirstNameChange={(event: any) => {
                  handlePassengerFirstNameChange(event, index);
                }}
                onLastNameChange={(event: any) => {
                  handlePassengerLastNameChange(event, index);
                }}
                firstNameError={showValidationErrors && (passenger?.first_name?.length ?? 0) < 2}
                lastNameError={showValidationErrors && (passenger?.last_name?.length ?? 0) < 2}
              />
              <Grid item md={3} sm={12} xs={12} mb={{ xs: 2, md: 0 }}>
                <Select
                  name="passenger_type"
                  sx={{
                    width: "100%",
                    height: "43.5px",
                    borderRadius: 0,
                    fontFamily: "Neutraface2Book",
                    fontSize: "15px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "0.75px solid #575757 !important"
                    }
                  }}
                  value={passenger?.passenger_type}
                  onChange={event => handlePassengerTypeChange(index, event.target.value as string)}
                >
                  {offerPrices.map(offerPrice => (
                    <MenuItem key={offerPrice.id} value={offerPrice.external_reference}>
                      {offerPrice.description || offerPrice.external_reference}
                    </MenuItem>
                  ))}
                </Select>
                <Box sx={{ display: { sm: "flex", md: "none", fontSize: "11px" } }} mt={0.5} ml={0.5}>
                  {t("transport.formPassengerType")}
                </Box>
              </Grid>
              <Grid item md={3} sm={12} xs={12} sx={{ display: "flex" }}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: { sm: "flex-end", md: "flex-start" }
                  }}
                >
                  <Grid item ml={{ xs: 0, sm: 3 }}>
                    {passenger?.hasRemove && <PassengerRemoveButton onClick={() => removePassenger(index)} />}
                  </Grid>
                </Grid>
              </Grid>
              <PassengerRowMobileDivider />
            </Grid>
          );
        })}
    </PassengerFormShell>
  );
}
